import URI from 'urijs';
import domains from '@clubmed/domains-service';
import logger from 'Logger';
import { getGqlName, PAGE_SITEMAP } from 'Utils/request/requestDictionary';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
type SitemapOptions = {
  locale: string;
  originalUrl: string | null | undefined;
  userAgent?: string;
};
type SitemapUrl = {
  href: string;
};
type SitemapEntry = {
  changefreq: string;
  links?: Array<{
    lang: string;
    url: string;
  }>;
  mobile?: string;
  priority: number;
  url: string;
};
const SITEMAP_URL_CHANGE_FREQUENCY = 'weekly';
const SITEMAP_URL_PRIORITY = 0.5;
export const XML_NAMESPACES = [
  'xmlns="https://www.sitemaps.org/schemas/sitemap/0.9"',
  'xmlns:xhtml="https://www.w3.org/1999/xhtml"',
];
export function isBaiduBot(userAgent: string = ''): boolean {
  // Detects Baidu’s web crawler via the user agent string.
  // e.g. "Mozilla/5.0 (compatible; Baiduspider/2.0; +http://www.baidu.com/search/spider.html)"
  return new RegExp('Baiduspider', 'ig').test(userAgent);
}
export function getXmlNamespaces(userAgent?: string) {
  const xmlNamespaces = isBaiduBot(userAgent)
    ? [...XML_NAMESPACES, 'xmlns:mobile="https://www.baidu.com/schemas/sitemap-mobile/1/"']
    : XML_NAMESPACES;
  return xmlNamespaces.join(' ');
}
export async function getPageUrls(pageTypes: Array<string>, appContext: AppContext) {
  const pageTypesString = pageTypes.map((pageType) => `"${pageType}"`).join(',');
  const pagesQuery = `{
    pages: pagesByTypes(pageTypes: [${pageTypesString}]) {
      href: url
    }
  }`;

  try {
    const { pages } = await graphQLClient.runQuery(pagesQuery, appContext, {
      requestName: PAGE_SITEMAP,
    });
    return pages;
  } catch (error) {
    logger.error(error);
    return [];
  }
}
export function getPageEntries(
  dataEntries: Array<SitemapUrl>,
  { locale, originalUrl, userAgent }: SitemapOptions,
): Array<SitemapEntry> {
  const uri = new URI(originalUrl);
  const hostname = uri.hostname();
  const tld = uri.tld();
  const isDomainDefaultLocale = domains.getLocaleFromHostname(tld) === locale;
  return dataEntries.map(({ href }) => {
    // Build full entry url
    const entryUri = new URI(href).protocol('https').hostname(hostname).tld(tld);

    if (!isDomainDefaultLocale && locale) {
      entryUri.addQuery({
        locale,
      });
    }

    // Build complete entry
    return {
      url: entryUri.toString(),
      changefreq: SITEMAP_URL_CHANGE_FREQUENCY,
      priority: SITEMAP_URL_PRIORITY,
      ...(isBaiduBot(userAgent) && {
        mobile: 'pc mobile',
      }),
    };
  });
}
type Hreflang = {
  url: string;
  links: {
    url: string;
    lang: string;
  }[];
};
export async function getHreflangs(appContext: AppContext): Promise<Hreflang[]> {
  const query = `{
  hreflangs {
    url
    links {
      url
      lang
    }
  }
}`;

  try {
    const { hreflangs } = await graphQLClient.runQuery(query, appContext, {
      requestName: getGqlName(['CMS', 'HREFLANGS']),
    });
    return hreflangs;
  } catch (error) {
    logger.error(error);
    return [];
  }
}
